import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  card,
  cardInner,
  cardTitle,
  cardTitleWrap,
  comingSoonTitle,
  cardsWrap,
  cardsWrapGrid,
} from "../styles/DashboardStyles";
import { fetchApps } from 'scripts/fetchApps'
import { fetchReports } from "scripts/fetchReports";
import { useNavigate } from "react-router-dom";
import CardsCarousel from "components/CardsCarousel";
import App from "interfaces/App.interface";
import ReportsTile from "components/ReportsTile";
import { ReportData } from "interfaces/ReportData.interface";
import ProfitLossTileModal from "components/ProfitLossTileModal";
import {
  fetchProfitLossTileEnabled,
  getStoredProfitLossTileEnabled,
} from "scripts/keyVaultSettings";
import { getStoredStoreNumber, validateProfitLossEmployeePin } from "scripts/profitLossApi";

const PROFIT_LOSS_TILE_IMAGE = "/assets/PnLNew.png";

const DashboardTiles: React.FC<{ user?: string }> = ({ user }) => {
  const [apps, setApps] = useState<App[] | undefined>([]);
  const [background, setBackground] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [guestConnectionCount, setGuestConnectionCount] = useState(0);
  const [profitLossOpen, setProfitLossOpen] = useState(false);
  const [profitLossAccessOpen, setProfitLossAccessOpen] = useState(false);
  const [employeePin, setEmployeePin] = useState("");
  const [profitLossAccessError, setProfitLossAccessError] = useState("");
  const [profitLossAccessLoading, setProfitLossAccessLoading] = useState(false);
  const [isProfitLossTileEnabled, setIsProfitLossTileEnabled] = useState(
    () => getStoredProfitLossTileEnabled() ?? true
  );
  const [selectedReportDate, setSelectedReportDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const navigate = useNavigate();

  const isGuestConnectionApp = (app: App) => {
    const name = (app.name || "").toLowerCase();
    return name.includes("guest connection");
  };

  const handleOpenProfitLossAccess = () => {
    setEmployeePin("");
    setProfitLossAccessError("");
    setProfitLossAccessOpen(true);
  };

  const handleCloseProfitLossAccess = () => {
    if (profitLossAccessLoading) return;
    setProfitLossAccessOpen(false);
    setEmployeePin("");
    setProfitLossAccessError("");
  };

  const handleValidateProfitLossAccess = async () => {
    const storeNumber = getStoredStoreNumber();

    if (!storeNumber) {
      setProfitLossAccessError("Store number is unavailable for this session.");
      return;
    }

    if (!employeePin.trim()) {
      setProfitLossAccessError("EmployeePin is required.");
      return;
    }

    setProfitLossAccessLoading(true);
    setProfitLossAccessError("");

    try {
      const isValid = await validateProfitLossEmployeePin({
        storeNumber,
        employeePin: employeePin.trim(),
      });

      if (!isValid) {
        setProfitLossAccessError("You are not authorized to access this resource.");
        return;
      }

      setProfitLossAccessOpen(false);
      setProfitLossOpen(true);
      setEmployeePin("");
    } catch (error) {
      setProfitLossAccessError(
        error instanceof Error ? error.message : "You are not authorized to access this resource."
      );
    } finally {
      setProfitLossAccessLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      const [appsResponse, isProfitLossEnabled] = await Promise.all([
        fetchApps(),
        fetchProfitLossTileEnabled(),
      ]);
      setApps(appsResponse?.filter((item: App) => !item?.name?.toLowerCase().includes('background')));
      setBackground(appsResponse?.filter((item: App) => item?.name?.toLowerCase().includes('background'))[0]?.image);
      setIsProfitLossTileEnabled(isProfitLossEnabled);
    };
    fetchData();
  }, []);

  React.useEffect(() => {
    if (!user) return;

    let isMounted = true;

    const loadProfitLossTileEnabled = async () => {
      const isEnabled = await fetchProfitLossTileEnabled();
      if (isMounted) setIsProfitLossTileEnabled(isEnabled);
    };

    loadProfitLossTileEnabled();

    return () => {
      isMounted = false;
    };
  }, [user]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const styleId = "guest-connection-badge-pulse";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes badgePulse {
        0% { box-shadow: 0 0 0 0 rgba(225, 6, 0, 0.55); }
        70% { box-shadow: 0 0 0 8px rgba(225, 6, 0, 0); }
        100% { box-shadow: 0 0 0 0 rgba(225, 6, 0, 0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const getLocalDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const isWithinPollingHours = () => {
      const now = new Date();
      const hour = now.getHours();
      return hour >= 8 && hour < 20;
    };

    const isTodaySelected = () => {
      return selectedReportDate === getLocalDateString(new Date());
    };

    const fetchReportData = async () => {
      setReportsLoading(true);
      const reportResponse = await fetchReports({
        startDate: selectedReportDate,
        endDate: selectedReportDate,
      });
      if (!isMounted) return;
      setReportData(reportResponse);
      setReportsLoading(false);
    };

    fetchReportData();

    intervalId = setInterval(() => {
      if (isWithinPollingHours() && isTodaySelected()) {
        fetchReportData();
      }
    }, 3600000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedReportDate]);

  React.useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const getStoreNumber = () => {
      try {
        return localStorage.getItem("storeNumber");
      } catch {
        return null;
      }
    };

    const fetchGuestConnectionTickets = async () => {
      const storeId = getStoreNumber();
      if (!storeId) {
        if (isMounted) setGuestConnectionCount(0);
        return;
      }

      const url =
        `https://api.edible.tech/zendesk/v1/gettickets?` +
        `storeId=${encodeURIComponent(storeId)}` +
        `&brand=31944278133140` +
        `&subscription-key=78824a5c6e674e6fa7f425abd31ab679`;

      try {
        const response = await fetch(url, { method: "GET" });
        if (!response.ok) {
          console.error("Guest Connection tickets error:", response.status, response.statusText);
          return;
        }

        const data = await response.json();
        const count = typeof data?.count === "number" ? data.count : 0;

        if (!isMounted) return;
        setGuestConnectionCount(count);
      } catch (error) {
        console.error("Error fetching Guest Connection tickets:", error);
      }
    };

    fetchGuestConnectionTickets();
    intervalId = setInterval(fetchGuestConnectionTickets, 300000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!apps) return null;

  const appsWithBadges = apps.map((app) =>
    isGuestConnectionApp(app) ? { ...app, badgeCount: guestConnectionCount } : app
  );
  const activeApps = [
    ...appsWithBadges.filter(app => app.isActive !== false),
    ...(isProfitLossTileEnabled ? [{
      name: "Profit & Loss",
      image: PROFIT_LOSS_TILE_IMAGE,
      link: "#",
      onClick: handleOpenProfitLossAccess,
    }] : []),
  ];
  const comingSoonApps = appsWithBadges.filter(app => app.isActive === false);

  return (
    <>
      <CardsCarousel
        apps={activeApps}
        user={user || ""}
        card={card}
        cardInner={cardInner}
        cardTitleWrap={cardTitleWrap}
        cardTitle={cardTitle}
        navigate={navigate}
        containerStyle={comingSoonApps && comingSoonApps.length > 0 ?
          ({...cardsWrap, background: `url(${background}) center/cover no-repeat`}) :
          ({...cardsWrapGrid, background: `url(${background}) center/cover no-repeat`})}
      />
      <ReportsTile
        report={reportData}
        loading={reportsLoading}
        selectedDate={selectedReportDate}
        onDateChange={setSelectedReportDate}
        autoCollapsed={true}
      />
      {comingSoonApps && comingSoonApps.length > 0 && (
        <>
          <div style={comingSoonTitle}>Coming Soon</div>
          <CardsCarousel
            apps={comingSoonApps}
            user={user || ""}
            card={card}
            cardInner={cardInner}
            cardTitleWrap={cardTitleWrap}
            cardTitle={cardTitle}
            navigate={navigate}
            containerStyle={{ ...cardsWrap, background: 'none', position: 'relative' }}
          />
        </>
      )}
      <ProfitLossTileModal
        open={profitLossOpen}
        onClose={() => setProfitLossOpen(false)}
      />
      <Dialog
        open={profitLossAccessOpen}
        onClose={handleCloseProfitLossAccess}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: "Poppins, sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          Profit &amp; Loss Access
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 14, color: "#2E2E2E" }}>
              Enter EmployeePin to access Profit &amp; Loss.
            </Typography>
            <TextField
              label="EmployeePin"
              type="password"
              value={employeePin}
              onChange={(event) => setEmployeePin(event.target.value)}
              fullWidth
              autoFocus
              disabled={profitLossAccessLoading}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleValidateProfitLossAccess();
                }
              }}
            />
            {profitLossAccessError && (
              <Alert severity="error">
                {profitLossAccessError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            onClick={handleCloseProfitLossAccess}
            disabled={profitLossAccessLoading}
            sx={{
              color: "#6D6D6D",
              fontFamily: "Poppins, sans-serif",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              void handleValidateProfitLossAccess();
            }}
            disabled={profitLossAccessLoading}
            sx={{
              backgroundColor: "#E10600",
              color: "#FFFFFF",
              fontFamily: "Poppins, sans-serif",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#B90500",
              },
            }}
          >
            {profitLossAccessLoading ? "Validating..." : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DashboardTiles;
